<template>
  <div class="app-layout">
    <div
      class="container searchcontainer mt-3 mx-auto"
      :class="{ active: isActive }"
    >
      <client-only>
        <TailwindComponentsLoading
          v-if="searchStore.isLoading"
          fullScreen
        ></TailwindComponentsLoading>

        <div class="search-form" :class="{ active: isActive }">
          <h2 v-if="!isActive" class="align-center text-center text-2xl">
            {{ $t('What secrets of the past are you seeking?') }}
          </h2>
          <SearchFormNew :name="'term'" :options="filterOptions" />
        </div>
        <div class="search-results">
          <div v-if="isWarning" class="message is-medium is-warning">
            <div class="message-body">
              <p>
                <i class="mdi mdi-alert-circle is-warning"></i>
                {{
                  $t(
                    'Forbidden characters will be removed from your search query.'
                  )
                }}
              </p>
            </div>
          </div>
          <div v-if="isError" class="message is-medium is-danger">
            <div class="message-body">
              <p>
                {{
                  $t(
                    'Your request timed out while searching for results. For more help see'
                  )
                }}
                <router-link :to="localePath({ name: 'help-usage' })">{{
                  $t('Usage')
                }}</router-link>
              </p>
            </div>
          </div>

          <div v-if="!isVoid" class="mt-5">
            <div v-if="!hasMatches" style="margin-top: 1rem">
              <div class="message is-medium">
                <div class="message-body">
                  {{ $t('Search returned no results') }} …
                </div>
              </div>
            </div>
            <div v-else class="mx-auto">
              <Suspense>
                <div
                  class="grid grid-cols-1 items-start gap-4 lg:grid-cols-6 lg:gap-8"
                >
                  <!-- Left column -->
                  <div class="grid grid-cols-1" v-if="titles?.length > 0">
                    <section aria-labelledby="section-2-title">
                      <SearchFacet
                        @input="setTitle"
                        :items="titles"
                        :title="'Document'"
                      />
                    </section>
                  </div>
                  <div
                    :class="{
                      'grid gap-4 lg:col-span-5': titles?.length > 0,
                      'grid gap-4 lg:col-span-6': titles?.length === 0,
                    }"
                  >
                    <div
                      class="bg-[#f0f0f0] text-center p-4 font-semibold rounded flex justify-between items-center"
                    >
                      <div class="has-text-weight-bold">
                        {{ $n(total) }}
                        {{ total === 1 ? $t('Result') : $t('Results') }}
                      </div>
                      <BasePagination
                        style="margin-top: 0rem !important"
                        :total="total"
                        :current.sync="currentPage"
                        @changePage="onChangePage"
                        :per-page="itemsPerPage"
                        :showPerPage="true"
                        @update:perPage="handlePerPageUpdate"
                      ></BasePagination>
                    </div>
                    <SearchResults
                      v-if="hasMatches"
                      :results="results"
                      :query="QUERY"
                    />
                  </div>
                </div>
              </Suspense>
            </div>
          </div>
        </div>
      </client-only>
    </div>
  </div>
</template>

<script>
// TODO: ensure that history.popState is not broken by url param correction (can breaking it be avoided at all??), can use .replace when fixing invalid params?
// TOOD: fix pagination behavior, do not show page change until after results have finished loading
import omit from 'lodash/omit'

const QUERY_PARAMS = Object.freeze({
  FUZZINESS: 'fuzziness',
  TERM: 'term',
  PAGE: 'page',
  VIEW: 'view',
  SIZE: 'size',
  SORT: 'sort',
  COLID: 'colId',
  DOCID: 'docId',
  COLNAME: 'f_collectionIdName',
  AUTHOR: 'f_author',
  UPLOADER: 'f_uploader',
  TITLE: 'f_title',
  SCRIPT: 'scriptType',
  FILTER: Object.freeze({
    SCORE: 'score',
  }),
  RESULT: 'result',
  SEARCH: 'search',
})

const BAD_SYMBOLS = '(,[,+,-,:,=,],),#,~,"'

export default {
  setup() {
    definePageMeta({
      layout: 'base',
      middleware: 'auth',
    })
    const searchStore = useSearchStore()
    const searchApi = useSearch()
    const transformComp = useTransform()
    return {
      searchStore,
      searchApi,
      transformComp,
    }
  },
  provide: {
    QUERY: QUERY_PARAMS,
  },
  watch: {
    '$route.query': {
      handler: 'update',
      // NOTE: should NOT run before children's watchers
      immediate: false,
    },
    fuzziness() {
      this.$router.push(
        this.localePath({
          query: {
            ...this.$route.query,
            fuzziness: this.fuzziness,
          },
        })
      )
    },
    offset() {
      this.submit()
    },
    itemsPerPage() {
      this.submit()
    },
    currentPage(value) {
      const sanitizedValue = Math.min(
        value,
        Math.ceil(this.total / this.itemsPerPage)
      )

      if (value === 1) {
        this.$router.push({
          query: omit(this.$route.query, QUERY_PARAMS.PAGE),
        })
      } else {
        const query = {
          ...this.$route.query,
          [QUERY_PARAMS.PAGE]: value,
        }
        if (value !== value) {
          this.$router.replace({ query })
        } else {
          this.$router.push({ query })
        }
      }
      this.offset = (value - 1) * this.itemsPerPage
    },
  },
  data: () => ({
    fuzziness: 1,
    isWarning: false,
    total: 0,
    offset: 0,
    results: null,
    isError: false,
    isActive: false,
    facets: null,
    mobile: 'reduce',
    originalOptions: null,
    pendingQuery: {},
    itemsPerPage: 12,
    currentPage: 1,
    selectedOptions: {
      colId: [],
      docId: [],
      f_author: [],
      f_uploader: [],
      f_title: [],
      scriptType: [],
      f_collectionName: [],
    },
  }),
  computed: {
    isVoid() {
      return this.results === null
    },
    hasMatches() {
      return this.results instanceof Array ? this.results.length > 0 : false
    },
    hasFilters() {
      return Object.values(this.QUERY.FILTER).some(
        name => this.$route.query[name]
      )
    },
    isFiltersVisible() {
      return this.hasMatches || this.hasFilters
    },
    titles() {
      if (this.facets?.facet) {
        let found = this.facets?.facet?.filter(item => {
          if (item.facet_field === 'f_title') {
            return item
          }
        })
        let arrTemp = this.facets?.facet?.filter(item => {
          if (item.facet_field === 'f_title') {
            return item
          }
        })[0].facetMap?.entry
        return arrTemp.map(item => ({ value: item.key, count: item.value }))
      }
      return []
    },
    filterOptions() {
      if (!this.facets) {
        return []
      }

      let options = []
      for (const facet of this.facets?.facet) {
        let items = facet.facetMap.entry.map(item => ({
          name: item.key,
          value: item.key,
        }))
        options.push({
          option: facet.facet_field,
          label: this.facetLabel(facet.facet_field),
          items: items,
        })
      }

      options.push({
        option: this.QUERY.FUZZINESS,
        label: 'Fuzziness',
        type: 'radio',
        items: [
          { name: 'None', value: '0' },
          { name: 'Medium', value: '1' },
          { name: 'High', value: '2' },
        ],
      })

      return options
    },
  },
  created() {
    this.QUERY = QUERY_PARAMS
    this.defaultItemsPerPage = 12
  },
  async mounted() {
    this.$setPageTitle(this.$t('Search'))
    this.isActive = false
    if (!process.server) {
      this.update(this.$route.query)
    }
  },
  methods: {
    update(query, prevQuery = {}) {
      let mustReload = false
      let mustResetPage = true
      let mustReset = false

      if (query[this.QUERY.TERM] != prevQuery[this.QUERY.TERM]) {
        mustReload = true
      }

      if (query[this.QUERY.FUZZINESS] != prevQuery[this.QUERY.FUZZINESS]) {
        mustReload = true
      }

      if (!query[this.QUERY.TERM]) {
        mustReset = true
      }

      if (query[this.QUERY.PAGE] != prevQuery[this.QUERY.PAGE]) {
        mustReload = true
        mustResetPage = false
      }

      if (query[this.QUERY.COLID] != prevQuery[this.QUERY.COLID]) {
        mustReload = true
        mustResetPage = false
      }

      if (query[this.QUERY.DOCID] != prevQuery[this.QUERY.DOCID]) {
        mustReload = true
        mustResetPage = false
      }

      if (query[this.QUERY.COLNAME] != prevQuery[this.QUERY.COLNAME]) {
        mustReload = true
        mustResetPage = false
      }

      if (query[this.QUERY.AUTHOR] != prevQuery[this.QUERY.AUTHOR]) {
        mustReload = true
        mustResetPage = false
      }

      if (query[this.QUERY.UPLOADER] != prevQuery[this.QUERY.UPLOADER]) {
        mustReload = true
        mustResetPage = false
      }

      if (query[this.QUERY.TITLE] != prevQuery[this.QUERY.TITLE]) {
        mustReload = true
        mustResetPage = false
      }

      if (query[this.QUERY.SCRIPT] != prevQuery[this.QUERY.SCRIPT]) {
        mustReload = true
        mustResetPage = false
      }

      if (query[this.QUERY.SIZE] != prevQuery[this.QUERY.SIZE]) {
        mustReload = mustResetPage = true
      }

      for (const value of Object.values(this.QUERY.FILTER)) {
        if (query[value] != prevQuery[value]) {
          mustReload = mustResetPage = true
        }
      }

      // do not reload anything if view changed
      if (query[this.QUERY.VIEW] != prevQuery[this.QUERY.VIEW]) {
        if (mustReload === false && mustReset === false) {
          mustResetPage = false
        }
      }

      // if (query[this.QUERY.PAGE]) {
      //   this.offset = this.itemsPerPage * query[this.QUERY.PAGE]
      // }

      if (mustResetPage) {
        this.resetPage()
      }

      if (mustReset) {
        this.reset()
      } else if (mustReload) {
        this.submit()
      }
    },
    submit: useDebounce(async function () {
      this.isActive = true
      this.searchStore.isLoading = true

      this.isError = false

      const params = {
        ...this.$route.query,
      }
      let data
      let filters = []

      const queryParams = this.$route.query
      const selectedOptions = this.selectedOptions

      const filterMappings = {
        [this.QUERY.COLID]: 'colId',
        [this.QUERY.DOCID]: 'docId',
        [this.QUERY.AUTHOR]: 'f_author',
        [this.QUERY.UPLOADER]: 'f_uploader',
        [this.QUERY.TITLE]: 'f_title',
        [this.QUERY.SCRIPT]: 'scriptType',
        [this.QUERY.COLNAME]: 'f_collectionName',
      }

      for (const [param, filterKey] of Object.entries(filterMappings)) {
        if (queryParams[param] != null) {
          const paramValue = queryParams[param]
          const filterValue = `${filterKey}:"${paramValue}"`
          filters.push(filterValue)

          if (Array.isArray(paramValue)) {
            paramValue.forEach(value => {
              if (!selectedOptions[filterKey].includes(value)) {
                selectedOptions[filterKey].push(value)
              }
            })
          } else {
            if (!selectedOptions[filterKey].includes(paramValue)) {
              selectedOptions[filterKey].push(paramValue)
            }
          }
        }
      }

      let term = params[this.QUERY.TERM]

      this.isWarning = false

      // add warning when bad symbols are found
      BAD_SYMBOLS.split(',').forEach(c => {
        if (term.includes(c)) {
          this.isWarning = true
        }
      })

      // replace all bad symbols
      term = params[this.QUERY.TERM]
      BAD_SYMBOLS.split(',').forEach(c => {
        term = term.replace(new RegExp('\\' + c, 'g'), '')
      })

      // add fuzziness to all terms

      //if the fuzziness comes with param, else use this.fuzziness
      let fuzziness = params[this.QUERY.FUZZINESS] || 1
      term = term
        .split(' ')
        .map(t => t + '~' + fuzziness)
        .join(' && ')

      try {
        data = await this.searchApi.fetchSearchFulltext({
          term: term,
          start: this.offset,
          // rows: params[this.QUERY.SIZE] || this.defaultItemsPerPage,
          rows: this.itemsPerPage,
          filter: filters,
        })

        this.facets = data.facets
        this.facets.facet = this.facets.facet.filter(
          fac =>
            [
              'f_author',
              'f_title',
              'f_uploader',
              'f_collectionName',
              'colId',
            ].indexOf(fac.facet_field) >= 0
        )

        let transformData = this.transformComp.transform(data)
        this.results = transformData.items
        this.total = data.numResults

        this.scroll()
      } catch (err) {
        console.log(err)
        this.isError = true
        this.results = null
      }
      this.searchStore.isLoading = false
    }, 25),
    resetFilters() {
      this.selectedOptions = {
        colId: [],
        f_author: [],
        f_uploader: [],
        f_title: [],
        scriptType: [],
        f_collectionName: [],
      }
      this.$router.replace({ query: { term: this.$route.query.term } })
    },
    resetPage() {
      this.$router.push({
        query: omit(this.$route.query, this.QUERY.PAGE),
      })
    },
    onChangePage(value) {
      this.currentPage = value
      // if (value === 1) {
      //   this.$router.push({
      //     query: omit(this.$route.query, QUERY_PARAMS.PAGE),
      //   })
      // } else {
      //   this.$router.push({
      //     query: {
      //       ...this.$route.query,
      //       [QUERY_PARAMS.PAGE]: value,
      //     },
      //   })
      // }
    },
    setTitle(item) {
      let query = { ...this.$route.query }
      if (item !== null && item.length > 0) {
        query['f_title'] = item
      } else {
        query = omit(query, 'f_title')
      }
      // Wait for all filter to be set by search button
      this.$router.push({ query })
    },
    getFilteredOption(option, searchValue) {
      let fieldKey = Object.keys(option)
      let items = option[Object.keys(option)[0]]
      if (this.originalOptions === null) {
        this.originalOptions = {}
      }

      if (!this.originalOptions.hasOwnProperty(fieldKey)) {
        this.originalOptions[fieldKey] = [...items]
      }

      if (searchValue === '') {
        items = [...this.originalOptions[fieldKey]]
      } else {
        items = items.filter(item => item.value.includes(searchValue))
      }

      option[Object.keys(option)[0]] = items
    },
    handlePerPageUpdate(newPerPage) {
      this.itemsPerPage = parseInt(newPerPage)
    },
    getItems(option) {
      let items = option.facetMap.entry
      const formattedItems = items?.map(item => `${item.key}`)
      return formattedItems
    },
    selectFilter(option, item) {
      let fieldKey = option.facet_field

      let query = { ...this.$route.query }
      if (item !== null && item.length > 0) {
        query[fieldKey] = item
      } else {
        query = omit(query, fieldKey)
      }
      // Wait for all filter to be set by search button
      this.$router.push({ query })

      this.pendingQuery = query
      this.setSelectedOptions(fieldKey, option, item)
    },
    setSelectedOptions(label, option, selection) {
      this.selectedOptions[label] = selection
      // this.selectedOptions = {
      //   ...this.selectedOptions,
      //   [label]: selection,
      // }
    },
    reset() {
      this.$router.push({
        query: omit(this.$route.query, [
          this.QUERY.TERM,
          this.QUERY.PAGE,
          this.QUERY.FUZZINESS,
        ]),
      })

      this.results = null
    },
    scroll() {
      this.$nextTick(() => (document.documentElement.scrollTop = 0))
    },
    facetLabel(label) {
      switch (label) {
        case 'f_uploader':
          return this.$t('Uploader')
          break
        case 'f_author':
          return this.$t('Author')
        case 'f_title':
          return this.$t('Title')
        case 'scriptType':
          return this.$t('Script type')
        case 'f_collectionName':
          return this.$t('Collection Name')
        case 'colId':
          return this.$t('Collection ID')
        default:
          return label
      }
    },
  },
}
</script>

<style>
.column.is-row {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
}

.search-results-wrapper {
  padding-bottom: 2rem;
}
</style>

<style scoped>
.searchcontainer {
  position: relative;
  display: flex;
  justify-content: center;
  flex-direction: row;
  min-height: 100vh;
  max-width: 90%;
}

.searchcontainer.active {
  flex-direction: column;
  justify-content: normal;
}

.search-results {
  justify-content: center;
  align-items: center;
}

.search-form {
  display: flex;
  flex-direction: column;
  margin-top: 20%;
  max-width: 100%;
  transition: transform 0.5s ease, margin-top 0.5s ease; /* Add transition for smooth animation */
  width: 764px;
  text-wrap: balance;
}

.search-form.active {
  top: 0;
  margin-top: 12px; /* Ensure there's no margin at the top when active */
  width: 100%;
  max-width: 100%;
}
</style>
